import { useState, useRef, useEffect, Ref } from 'react';
import styled from 'styled-components';
import {
  colors,
  shadows,
  radius,
  space,
  easingTokens,
  transitionTokens,
} from '@ardoq/design-tokens';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { ellipsisCSS, focusOutline } from '@ardoq/style-helpers';
import { TextOverflowEllipsis } from '@ardoq/popovers';
import { useResizeObserver } from '@ardoq/hooks';
import { buttonTypography } from './Button';
import { ButtonSize } from './types';

type SlidingIndicatorProps = {
  $left: number;
  $width: number;
  $animate: boolean;
};

const SlidingIndicator = styled.div<SlidingIndicatorProps>`
  position: absolute;
  top: ${space.xxsmall};
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  height: calc(100% - ${space.xxsmall} * 2);
  background: ${colors.surfaceActionTertiary};
  border: 1px solid ${colors.borderAction};
  border-radius: 6px;
  box-shadow: ${shadows.shadowXS};
  transition: ${({ $animate }) =>
    $animate
      ? `left ${transitionTokens.medium} ${easingTokens.cubicInOut}, width ${transitionTokens.medium} ${easingTokens.cubicInOut}`
      : 'none'};
  z-index: 1;
`;

type StyledSegmentedControlButtonProps = {
  $isSelected: boolean;
  $iconOnly: boolean;
  $buttonSize?: ButtonSize;
};

const StyledSegmentedControlButton = styled.button<StyledSegmentedControlButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ $iconOnly }) =>
    $iconOnly ? space.xsmall : `0 ${space.small}`};
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: ${colors.textDefault};
  transition: background-color ${transitionTokens.medium} ease;
  ${buttonTypography}
  height: 30px;
  ${({ $iconOnly }) => $iconOnly && `width: 30px;`}
  cursor: pointer;
  user-select: none;

  ${ellipsisCSS}

  &:focus-visible {
    ${focusOutline}
  }

  &:hover {
    background: ${({ $isSelected }) =>
      $isSelected ? 'transparent' : colors.surfaceActionGhostHover};
  }

  &:disabled {
    pointer-events: none;
    color: ${colors.textDisabled};
  }

  ${({ $isSelected }) =>
    $isSelected &&
    `
    z-index: 2;
  `}
`;

const StyledSegmentedControlButtonContent = styled.div`
  width: 100%;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space.xsmall};
`;

type SegmentedControlButtonProps = {
  selectedId?: string;
  item: SegmentedControlItem;
  onClick: () => void;
  variant: 'regular' | 'icon-only';
  disabled?: boolean;
};

const SegmentedControlButton = ({
  selectedId,
  item,
  onClick,
  variant,
  disabled,
  ref,
}: SegmentedControlButtonProps & { ref: Ref<HTMLButtonElement> }) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const shouldShowTooltip = variant === 'icon-only' || isOverflowed;
  return (
    <StyledSegmentedControlButton
      ref={ref}
      role="radio"
      $isSelected={selectedId === item.id}
      $iconOnly={variant === 'icon-only'}
      aria-checked={selectedId === item.id}
      onClick={onClick}
      disabled={disabled}
      type="button"
      data-tooltip-text={shouldShowTooltip ? item.label : undefined}
      aria-label={variant === 'icon-only' ? item.label : undefined}
    >
      <StyledSegmentedControlButtonContent>
        {item.icon && (
          <Icon
            iconName={item.icon}
            iconSize={IconSize.SMALL}
            color={
              selectedId === item.id ? colors.iconActionActive : colors.icon
            }
          />
        )}
        {variant !== 'icon-only' && (
          <TextOverflowEllipsis onEllipsisChange={setIsOverflowed}>
            {item.label}
          </TextOverflowEllipsis>
        )}
      </StyledSegmentedControlButtonContent>
    </StyledSegmentedControlButton>
  );
};

type SegmentedControlContainerProps = {
  $iconOnly: boolean;
  $itemCount: number;
};

const SegmentedControlContainer = styled.div<SegmentedControlContainerProps>`
  position: relative;
  isolation: isolate;
  background: ${colors.componentSegmentedControlSurfaceDefault};
  border-radius: ${radius.medium};
  padding: ${space.xxsmall};
  width: ${({ $iconOnly }) => ($iconOnly ? 'fit-content' : '100%')};
  display: grid;
  grid-template-columns: ${({ $iconOnly, $itemCount }) =>
    $iconOnly
      ? `repeat(${$itemCount}, min-content)`
      : `repeat(${$itemCount}, 1fr)`};
  gap: ${space.xsmall};
`;

type SegmentedControlItem<T extends string = string> = {
  id: T;
  label: string;
  icon?: IconName;
  disabled?: boolean;
};

type SlidingIndicatorState = {
  left: number;
  width: number;
  animate: boolean;
};

type UseSlidingIndicatorParams = {
  selectedId?: string;
  items: SegmentedControlItem[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  buttonRefs: React.RefObject<{
    [key: string]: HTMLButtonElement | null;
  }>;
};

const useSlidingIndicator = ({
  selectedId,
  items,
  containerRef,
  buttonRefs,
}: UseSlidingIndicatorParams): SlidingIndicatorState => {
  const [indicatorProps, setIndicatorProps] = useState<SlidingIndicatorState>({
    left: 0,
    width: 0,
    animate: false,
  });
  const { width: containerWidth } = useResizeObserver(containerRef);
  const isFirstRender = useRef(true);
  const previousContainerWidth = useRef(containerWidth);

  useEffect(() => {
    const isResizing = previousContainerWidth.current !== containerWidth;
    previousContainerWidth.current = containerWidth;

    if (selectedId && buttonRefs.current[selectedId] && containerRef.current) {
      const button = buttonRefs.current[selectedId];
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();

      const left = buttonRect.left - containerRect.left;
      const width = buttonRect.width;

      setIndicatorProps({
        left,
        width,
        animate: !isFirstRender.current && !isResizing,
      });

      if (isFirstRender.current) {
        isFirstRender.current = false;
      }
    }
  }, [selectedId, items, containerWidth, buttonRefs, containerRef]);

  return indicatorProps;
};

export type SegmentedControlProps<T extends string> = {
  items: SegmentedControlItem<T>[];
  selectedId: T;
  onSelectionChange: (id: T) => void;
  disabled?: boolean;
  variant?: 'regular' | 'icon-only';
};

export const SegmentedControl = <T extends string>({
  items,
  selectedId,
  onSelectionChange,
  disabled,
  variant,
}: SegmentedControlProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const indicatorProps = useSlidingIndicator({
    selectedId,
    items,
    containerRef,
    buttonRefs,
  });

  return (
    <SegmentedControlContainer
      ref={containerRef}
      role="radiogroup"
      $iconOnly={variant === 'icon-only'}
      $itemCount={items.length}
    >
      <SlidingIndicator
        $left={indicatorProps.left}
        $width={indicatorProps.width}
        $animate={indicatorProps.animate}
      />
      {items.map(item => (
        <SegmentedControlButton
          key={item.id}
          ref={(el: HTMLButtonElement | null) => {
            buttonRefs.current[item.id] = el;
          }}
          onClick={() => onSelectionChange(item.id)}
          disabled={disabled || item.disabled}
          selectedId={selectedId}
          item={item}
          variant={variant ?? 'regular'}
        />
      ))}
    </SegmentedControlContainer>
  );
};
